import { Difficulty } from '@/common/constants';
import api from './api';

export interface Exercise {
  id: string;
  title: string;
  description: string;
  type: 'grammar' | 'vocabulary' | 'listening' | 'reading' | 'speaking' | 'writing';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  category: string;
  duration: number; // in seconds
  questions: Question[];
  isCompleted: boolean;
  score?: number;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Question {
  id: string;
  question: string;
  type: 'multiple-choice' | 'fill-blank' | 'true-false' | 'essay' | 'speaking';
  options?: string[];
  correctAnswer?: string | number;
  explanation?: string;
  points: number;
}

export interface SpeakingExercise {
  id: string;
  title: string;
  description: string;
  category: 'pronunciation' | 'conversation' | 'reading-aloud' | 'storytelling';
  difficulty: Difficulty;
  duration: number;
  text?: string; // For reading aloud
  prompt?: string; // For conversation/storytelling
  targetWords?: string[]; // For pronunciation practice
  isCompleted: boolean;
  isFavorite: boolean;
  score?: number;
  completedAt?: string;
  cefr_level?: string;
  target_text?: string;
  estimated_duration_minutes?: number;
  xp_reward?: number;
}

export interface ExerciseFilters {
  type?: string;
  difficulty?: string;
  category?: string;
  isCompleted?: boolean;
  search?: string;
}

export interface ExerciseResponse {
  exercises: Exercise[];
  total: number;
  page: number;
  totalPages: number;
}

export interface SpeakingExerciseResponse {
  exercises: SpeakingExercise[];
  total: number;
  page: number;
  totalPages: number;
}

export interface ExerciseSubmission {
  exerciseId: string;
  answers: {
    questionId: string;
    answer: string | number;
  }[];
  timeSpent: number;
}

export interface ExerciseResult {
  id: string;
  exerciseId: string;
  score: number;
  totalPoints: number;
  percentage: number;
  timeSpent: number;
  answers: {
    questionId: string;
    answer: string | number;
    isCorrect: boolean;
    correctAnswer?: string | number;
    explanation?: string;
  }[];
  completedAt: string;
}

export interface SpeakingSubmission {
  exerciseId: string;
  audioBlob?: Blob;
  audioUrl?: string;
  timeSpent: number;
}

export interface SpeakingResult {
  id: string;
  exerciseId: string;
  score: number;
  pronunciation: {
    accuracy: number;
    fluency: number;
    completeness: number;
  };
  feedback: string[];
  suggestions: string[];
  completedAt: string;
}

export interface PaginationParams {
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

class ExerciseService {
  // Regular Exercises
  async getExercises(filters?: ExerciseFilters, pagination?: PaginationParams): Promise<ExerciseResponse> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    if (pagination) {
      Object.entries(pagination).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(`/exercises?${params.toString()}`);
    return response.data;
  }

  async getExerciseById(id: string): Promise<Exercise> {
    const response = await api.get(`/exercises/${id}`);
    return response.data;
  }

  async submitExercise(submission: ExerciseSubmission): Promise<ExerciseResult> {
    const response = await api.post(`/exercises/${submission.exerciseId}/submit`, submission);
    return response.data;
  }

  async getExerciseResult(exerciseId: string, resultId: string): Promise<ExerciseResult> {
    const response = await api.get(`/exercises/${exerciseId}/results/${resultId}`);
    return response.data;
  }

  async getExerciseResults(
    exerciseId?: string,
    pagination?: PaginationParams,
  ): Promise<{
    results: ExerciseResult[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (exerciseId) params.append('exerciseId', exerciseId);
    if (pagination) {
      Object.entries(pagination).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const response = await api.get(`/exercises/results?${params.toString()}`);
    return response.data;
  }

  // Speaking Exercises
  async getSpeakingExercises(
    filters?: ExerciseFilters,
    pagination?: PaginationParams,
  ): Promise<SpeakingExerciseResponse> {
    const params = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    if (pagination) {
      Object.entries(pagination).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }

    const response = await api.get(`/exercises/speaking?${params.toString()}`);
    return response.data;
  }

  async getSpeakingExerciseById(id: string): Promise<SpeakingExercise> {
    const response = await api.get(`/exercises/speaking/${id}`);
    return response.data;
  }

  async submitSpeakingExercise(submission: SpeakingSubmission): Promise<SpeakingResult> {
    const formData = new FormData();
    formData.append('exerciseId', submission.exerciseId);
    formData.append('timeSpent', submission.timeSpent.toString());

    if (submission.audioBlob) {
      formData.append('audio', submission.audioBlob, 'recording.wav');
    } else if (submission.audioUrl) {
      formData.append('audioUrl', submission.audioUrl);
    }

    const response = await api.post(`/exercises/speaking/${submission.exerciseId}/submit`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async toggleSpeakingFavorite(id: string): Promise<SpeakingExercise> {
    const response = await api.patch(`/exercises/speaking/${id}/favorite`);
    return response.data;
  }

  async getSpeakingResults(
    exerciseId?: string,
    pagination?: PaginationParams,
  ): Promise<{
    results: SpeakingResult[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (exerciseId) params.append('exerciseId', exerciseId);
    if (pagination) {
      Object.entries(pagination).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });
    }
    const response = await api.get(`/exercises/speaking/results?${params.toString()}`);
    return response.data;
  }

  // Assessment/Testing
  async getAssessmentQuestions(): Promise<Question[]> {
    const response = await api.get('/exercises/assessment');
    return response.data;
  }

  async submitAssessment(answers: { questionId: string; answer: string | number }[]): Promise<{
    level: string;
    score: number;
    recommendations: string[];
    strengths: string[];
    weaknesses: string[];
  }> {
    const response = await api.post('/exercises/assessment/submit', { answers });
    return response.data;
  }

  // Categories and Metadata
  async getExerciseCategories(): Promise<string[]> {
    const response = await api.get('/exercises/categories');
    return response.data;
  }

  async getExerciseTypes(): Promise<string[]> {
    const response = await api.get('/exercises/types');
    return response.data;
  }
}

export default new ExerciseService();
